import { useEffect, useState } from "react";

import { CardsService } from "../services/cardsService";
import type { CardTransferTarget } from "../types/cards-api.types";
import { useOrganizationWalletBalance } from "./useOrganizationWalletBalance";

// Wallet balance comes from the shared singleton hook so this page does not
// re-fetch what the cards listing already loaded.
export function useCardDetailSidebarData(
  cardId: string | undefined,
  refreshKey: number,
): {
  walletAvailable: number;
  transferTargets: CardTransferTarget[];
  isLoadingTransferTargets: boolean;
  refreshWalletBalance: () => Promise<void>;
} {
  const { balance, refresh: refreshWalletBalance } = useOrganizationWalletBalance();

  const walletAvailable = (() => {
    const raw = balance?.current?.balance;
    if (raw == null) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  })();

  const [transferTargets, setTransferTargets] = useState<CardTransferTarget[]>([]);
  const [isLoadingTransferTargets, setIsLoadingTransferTargets] = useState(false);

  useEffect(() => {
    if (!cardId) return;
    let cancelled = false;
    setIsLoadingTransferTargets(true);
    (async () => {
      try {
        const targets = await CardsService.fetchTransferTargets(cardId);
        if (!cancelled) setTransferTargets(targets);
      } catch {
        if (!cancelled) setTransferTargets([]);
      } finally {
        if (!cancelled) setIsLoadingTransferTargets(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, refreshKey]);

  return {
    walletAvailable,
    transferTargets,
    isLoadingTransferTargets,
    refreshWalletBalance,
  };
}
